import { StateBadge } from "@/components/session";
import { DataTable, type DataTableColumn } from "@/components/table";
import { Dash, ErrorNote, Hint } from "@/components/ui";
import { errorMessage } from "@/lib/api";
import { date, money, percent } from "@/lib/format";
import { useCyclePayouts } from "@/lib/queries";
import type { CyclePayout } from "@/lib/types";

/*
 * I PAYOUT DEL CICLO, SOLA LETTURA.
 *
 * Nessun pulsante, e non è una dimenticanza: un payout si registra dalla Fase
 * («Ho chiesto il payout», poi «Il payout è arrivato»), che chiude e collega il
 * periodo. Farlo avanzare da qui scriverebbe l'incasso nel registro senza
 * chiudere il periodo, e i numeri del ciclo si allontanerebbero dal metodo.
 *
 * Lordo e split sono facoltativi: dove chi ha registrato conosceva solo il
 * netto, c'è un trattino, non un numero uguagliato al netto.
 */

export function PayoutsSection({ cycleId }: { cycleId: string }) {
  const payouts = useCyclePayouts(cycleId);

  // Niente quote cliente/gestore: le righe di split non portano il `payoutId`,
  // e dividere il netto da qui sarebbe un calcolo nuovo. Stanno nel registro.
  const columns: DataTableColumn<CyclePayout>[] = [
    { key: "date", header: "Data", sortBy: (p) => p.requestedAt, cell: (p) => <span className="whitespace-nowrap" title="Data della richiesta">{date(p.requestedAt)}</span> },
    { key: "net", header: "Netto", align: "right", sortBy: (p) => Number(p.netAmount), cell: (p) => <span className="whitespace-nowrap">{money(p.netAmount, p.currency)}</span> },
    { key: "status", header: "Stato", sortBy: (p) => p.status, cell: (p) => <StateBadge kind="payout" state={p.status} /> },
  ];

  const detail = (p: CyclePayout) => {
    const t = p.transaction;
    return (
      <dl className="grid gap-x-8 gap-y-1 text-sm sm:grid-cols-2 lg:grid-cols-3">
        <Dettaglio label="Pagato il">{p.paidAt ? date(p.paidAt) : <Dash title="Non ancora pagato" />}</Dettaglio>
        <Dettaglio label="Lordo">{p.grossAmount === null ? <Dash title="Lordo non dichiarato" /> : money(p.grossAmount, p.currency)}</Dettaglio>
        <Dettaglio label="Split prop">{p.splitPct === null ? <Dash title="Percentuale non dichiarata" /> : percent(p.splitPct, 0)}</Dettaglio>
        <Dettaglio
          label="Nel registro"
          hint="L'importo della riga di registro nata quando il payout è stato pagato, nella valuta del tenant e al tasso congelato in quel momento."
        >
          {!t || t.amountReporting === null || !t.reportingCurrency ? (
            <Dash title="Nessuna riga di registro: il payout non è stato pagato" />
          ) : (
            <span title={t.fxAssumed ? "Tasso assunto, non misurato" : undefined}>
              {money(t.amountReporting, t.reportingCurrency)}
              {t.fxAssumed && <span className="ml-1 text-xs text-warn">tasso assunto</span>}
            </span>
          )}
        </Dettaglio>
        <div className="sm:col-span-2 lg:col-span-3">
          <Dettaglio label="Note">{p.notes ?? <Dash />}</Dettaglio>
        </div>
      </dl>
    );
  };

  return (
    <section>
      <h2 className="mb-2 flex items-center text-sm font-medium text-ink">
        Payout
        <Hint>Sola lettura. Un payout si registra dalla Fase: «Ho chiesto il payout», poi «Il payout è arrivato», che lo divide e chiude il periodo.</Hint>
      </h2>
      {payouts.isLoading ? (
        <p className="text-sm text-muted">Caricamento…</p>
      ) : payouts.error ? (
        <ErrorNote>{errorMessage(payouts.error)}</ErrorNote>
      ) : (
        <div className="card">
          <DataTable
            bare
            columns={columns}
            rows={payouts.data ?? []}
            rowKey={(p) => p.id}
            expandedContent={detail}
            initialSort={{ key: "date", dir: "desc" }}
            empty={{
              title: "Nessun payout",
              hint: "Nasce dalla Fase, quando la prop paga: apri la fase e premi «Ho chiesto il payout», poi «Il payout è arrivato».",
            }}
          />
        </div>
      )}
    </section>
  );
}

function Dettaglio({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-2">
      <dt className="text-xs text-faint">
        {label}
        {hint && <Hint>{hint}</Hint>}
      </dt>
      <dd className="text-ink">{children}</dd>
    </div>
  );
}
